import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Anchor, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { emitRefsChanged } from '../../../lib/generationEvents';
import { API, STAGES, fullImg } from './constants';

/** V15.21 — « Changer la référence » : choisir le rendu de l'étape qui servira de base à la suivante. */
export const ReferencePickerDialog = ({ open, onClose, projectId, lotId, stage, jobs, currentId, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const meta = STAGES.find((s) => s.id === stage);
  const candidates = (jobs || []).filter((j) => (j.params?.render_stage || 'full') === stage && j.status === 'completed' && !j.is_archived);
  const pick = async (job) => {
    setBusy(job.job_id);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/generations/${job.job_id}/stage-reference`, {}, { withCredentials: true });
      emitRefsChanged({ lotId, stage, anchored: !!r.data?.is_stage_reference });
      toast.success(r.data?.is_stage_reference ? `Nouvelle référence ${meta.short.toLowerCase()} ancrée` : 'Référence retirée');
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error('Ancrage impossible', { description: e?.response?.data?.detail || e?.message });
    } finally { setBusy(null); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl" style={{ background: 'var(--c-surface-0)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }} data-testid="reference-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]"><Anchor className="w-4 h-4" /> Référence de l'étape {meta?.n} · {meta?.label}</DialogTitle>
          <DialogDescription className="text-[11.5px]" style={{ color: 'var(--c-text-2)' }}>Le rendu ancré sert d'image de base à l'étape suivante. Cliquez un rendu pour l'ancrer (re-cliquer la référence actuelle la retire).</DialogDescription>
        </DialogHeader>
        {candidates.length === 0 ? <p className="text-[12px] py-6 text-center" style={{ color: 'var(--c-text-faint)' }}>Aucun rendu terminé à cette étape.</p> : (
          <div className="grid grid-cols-3 gap-2 max-h-[60vh] overflow-auto pr-1">
            {candidates.map((j) => {
              const isRef = j.job_id === currentId;
              return (
                <button key={j.job_id} type="button" onClick={() => pick(j)} disabled={!!busy} className="text-left rounded-lg overflow-hidden transition-colors"
                        style={{ border: `1px solid ${isRef ? 'var(--c-gold)' : 'var(--c-border)'}`, background: isRef ? 'rgba(217,160,91,0.08)' : 'rgba(255,255,255,0.025)' }}
                        data-testid={`reference-candidate-${j.job_id}`}>
                  <div className="relative aspect-[16/10]" style={{ background: '#fff' }}>
                    <img src={fullImg(j.image_url)} alt="" className="w-full h-full object-contain" />
                    {busy === j.job_id && <Loader2 className="absolute inset-0 m-auto w-5 h-5 animate-spin" style={{ color: '#0B1220' }} />}
                    {isRef && <span className="absolute top-1.5 left-1.5 text-[9.5px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(16,17,20,0.85)', color: '#F5C77E' }}>Référence actuelle</span>}
                  </div>
                  <div className="px-2 py-1.5 text-[10.5px] flex items-center justify-between" style={{ color: 'var(--c-text-2)' }}>
                    <span className="font-mono">{(j.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                    {typeof j.fidelity_score === 'number' && <span style={{ color: '#86EFAC' }}>Plan {j.fidelity_score} %</span>}
                    {j.is_validated && <span style={{ color: '#86EFAC' }}>validé</span>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReferencePickerDialog;
